/**
 * 提示词与模型（`#prompts`）——PR6 升级（判定卡 08、线框 04「保存流程」）。
 *
 * 刀 0 原样拷自 Loop 提示词页（来源见 COPIED_FROM.json）；本 PR 改动：
 * - 节点 = N2、N3（接续判定 N1 随注入来源退出，结构对齐 S1）；数据接 A-31 `GET /config`（配置项 `plan.prompt.*` ／ `plan.node.*.model`）。
 * - 每块一个实色「保存」→ 页头唯一实色「保存为新快照」（T-16）；改文本、换模型、重置为默认都只改草稿（D-27 判定总表 D 项），
 *   页级未保存提示条列改动项 ＋ 备注（可选）＋ 放弃全部；保存走 A-32，结果分成功 ／ 没有变化 ／ 冲突 ／ 校验不过 ／ 其他五种。
 * - 「重置为默认」＝ 把本块草稿填回默认值（默认值 ＝ 当前代码随发的默认配置，A-31 `defaults`，不是历史快照），不再立即保存。
 * - 合规：统一表格件、ui/Input ／ ui/Textarea、12 号下限、加载 ／ 失败态。
 */
import { AlertTriangle, PencilLine } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingInline } from "@/components/ui/feedback";
import { Input, Textarea } from "@/components/ui/input";
import { Select as StyledSelect } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import {
	type PlanConfig,
	planErrorCode,
	planErrorExtra,
	planErrorMessage,
	usePlanConfig,
	usePlanSnapshots,
	useSaveConfig,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import { PlanTable } from "../kit/plan-table";
import { SnapshotTag } from "../kit/snapshot-tag";
import { Pill } from "../trace-drawer/execution-drawer";
import {
	type SaveIssue,
	discardAll,
	discardDraft,
	getDraftState,
	patchDraftState,
	resetDrafts,
	setDraft,
	useDraftState,
} from "./draft-store";

export type PromptNodeKey = "signal" | "rank" | "n2" | "n3";
type Part = "system" | "user" | "jev" | "jev_guide" | "jev_knowledge";

/** 可在页面维护的大模型节点：信号、排序、N2、N3（结构对齐 S8：四个节点同一种注册，提示词与模型都在配置快照） */
export const PROMPT_NODE_KEYS: PromptNodeKey[] = ["signal", "rank", "n2", "n3"];
export const NODE_LABEL_ZH: Record<PromptNodeKey, { title: string; sub: string }> = {
	signal: { title: "信号", sub: "把客户这一轮的话拆成诉求清单，判每件怎么接上文" },
	rank: {
		title: "召回排序",
		sub: "只判每个候选工具是否覆盖这件事，给排序用；排序服务选 Jev 时不用下面的模型与提示词",
	},
	n2: { title: "N2 决策", sub: "每次只办计划里的一件事，决定这件事的下一步" },
	n3: { title: "N3 结果生成", sub: "按本轮计划状态出回复" },
};
export const PART_LABEL: Record<Part, string> = {
	system: "system（固定指令 · 稳定 · 可缓存）",
	user: "user（输入变量 ＋ 当前句）",
	jev: "Jev 题面（每个候选一道是非题；占位 {tool} {n} {what} {when} {when_not}）",
	jev_guide:
		"Jev 指南相关性题面（画像 pv5 起每条指南一道是非题，和工具题同一次调用；占位 {n} {title} {condition}）",
	jev_knowledge:
		"Jev 知识相关性题面（画像 pv5 起每条店铺知识一道是非题，和工具题同一次调用；占位 {n} {title} {content}）",
};

/** Jev 排序题面（基线升级第 4 批：之前写在代码里，页面看不到） */
const JEV_QUESTION_KEY = "plan.prompt.jev.question";
/** Jev 指南 ／ 知识相关性题面（标签体系 v2 批 4：指南 ／ 知识从意图卡口改相关性判断） */
const JEV_EXTRA_KEYS = [
	["jev_guide", "plan.prompt.jev.guide_question"],
	["jev_knowledge", "plan.prompt.jev.knowledge_question"],
] as const;

const promptKey = (node: PromptNodeKey, part: Part) => `plan.prompt.${node}.${part}`;
const modelKey = (node: PromptNodeKey) => `plan.node.${node}.model`;

const asText = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : JSON.stringify(v));

const VAR_RE = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;
function varsIn(text: string): string[] {
	return [...new Set([...text.matchAll(VAR_RE)].map((m) => m[1] ?? ""))].filter(Boolean);
}

function fmtAt(at: string | null | undefined) {
	return at ? at.slice(5, 16).replace("T", " ") : "—";
}

/** 默认值 ＝ 当前代码随发的默认配置（A-31 `defaults`，后端 `default_config()`，提示词即 prompts/*.txt、模型即代码里的默认）。
 * 不取历史种子快照：种子快照只在「还没人保存过」时才升级，保存过之后它就停在旧稿上。 */
function usePlanDefaults(): { content: Record<string, unknown> | null; failed: boolean } {
	const cfg = usePlanConfig();
	const content = cfg.data?.defaults;
	return {
		content: content && Object.keys(content).length ? content : null,
		failed: cfg.isError || (!!cfg.data && !(content && Object.keys(content).length)),
	};
}

function labelOf(cfg: PlanConfig | undefined, key: string) {
	return cfg?.schema.find((s) => s.key === key)?.label ?? key;
}

/** 页级保存（A-32）：页头按钮与提示条里的「重试」共用 */
function usePromptsSave() {
	const cfg = usePlanConfig();
	const save = useSaveConfig();
	const snaps = usePlanSnapshots();
	const { toast } = useToast();

	function run() {
		const st = getDraftState();
		const base = st.base ?? cfg.data?.snapshot_id;
		if (!cfg.data || !base || !Object.keys(st.drafts).length || st.saving) return;
		patchDraftState({ saving: true, issue: null });
		save.mutate(
			{
				base_snapshot_id: base,
				changes: st.drafts,
				note: st.note.trim() || undefined,
				operator: "admin-ui",
			},
			{
				onSuccess: async (d) => {
					await cfg.refetch();
					resetDrafts();
					if (d.unchanged) toast("没有变化，未生成新快照", "info");
					else toast(`已保存为快照 ${d.snapshot_id}，下一轮生效`, "success");
				},
				onError: (e) => {
					const code = planErrorCode(e);
					const extra = planErrorExtra(e);
					let issue: SaveIssue;
					if (code === "snapshot_conflict") {
						issue = { kind: "conflict", activeId: String(extra.active_snapshot_id ?? "") };
						snaps.refetch();
					} else if (code === "config_invalid" && Array.isArray(extra.items)) {
						issue = {
							kind: "invalid",
							items: (extra.items as { key: string; label?: string; reason: string }[]).map(
								(i) => ({
									key: i.key,
									label: i.label ?? labelOf(cfg.data, i.key),
									reason: i.reason,
								}),
							),
						};
					} else if (code === "note_required") {
						const labels = Array.isArray(extra.labels) ? (extra.labels as string[]) : [];
						issue = { kind: "note", labels };
					} else {
						issue = { kind: "error", message: planErrorMessage(e) };
					}
					patchDraftState({ saving: false, issue });
				},
			},
		);
	}
	return { run };
}

/** 页头唯一实色按钮（线框 04 §五）：由外壳放进 PageHeader */
export function PromptsSaveButton() {
	const cfg = usePlanConfig();
	const st = useDraftState();
	const { run } = usePromptsSave();
	const n = Object.keys(st.drafts).length;
	const reason = cfg.isError
		? "加载失败，刷新后再改"
		: st.saving
			? "正在保存"
			: !n
				? "没有未保存的修改"
				: null;
	return (
		<Button
			type="button"
			disabled={!!reason || !cfg.data}
			title={reason ?? `把 ${n} 处修改保存为新快照，下一轮生效`}
			onClick={run}
			data-testid="plan-prompts-save"
		>
			{st.saving ? "保存中…" : "保存为新快照"}
		</Button>
	);
}

/** ① 当前生效快照一行 */
function CurrentSnapshot({ cfg }: { cfg: PlanConfig }) {
	const snaps = usePlanSnapshots();
	const row = snaps.data?.rows.find((r) => r.is_active && r.snapshot_id === cfg.snapshot_id);
	return (
		<Card data-testid="plan-block-current">
			<CardContent className="space-y-1 px-4 py-3">
				<div
					className="flex flex-wrap items-center gap-2 text-sm"
					data-testid="plan-prompts-current"
				>
					<span className="text-text-secondary">当前生效</span>
					<SnapshotTag id={cfg.snapshot_id} />
					{row ? (
						<span className="text-xs text-text-muted">
							{fmtAt(row.at)} · {row.operator ?? "—"}
						</span>
					) : snaps.isError ? (
						<span className="text-xs text-text-muted">（保存时间与操作者没取到）</span>
					) : null}
					<span className="text-xs text-text-muted">（点快照号看这份配置）</span>
				</div>
				<p className="text-xs leading-5 text-text-muted">
					每个节点拆两段：system ＝ 固定指令（角色 ／ 规则 ／ 输出格式）；user ＝ 本轮输入的变量 ＋
					当前句。在 user 段写 {"{{变量名}}"} 即按运行值替换（字符串原样，对象 ／ 数组按
					JSON）；单个 {"{ }"} 不替换。
				</p>
			</CardContent>
		</Card>
	);
}

/** ② 未保存提示条（有改动才出现；吸顶） */
function UnsavedBar({ cfg }: { cfg: PlanConfig }) {
	const st = useDraftState();
	const { run } = usePromptsSave();
	const snaps = usePlanSnapshots();
	const cfgQ = usePlanConfig();
	const noteRef = useRef<HTMLInputElement | null>(null);
	const keys = Object.keys(st.drafts);
	const issue = st.issue;
	useEffect(() => {
		if (issue?.kind === "note") noteRef.current?.focus();
	}, [issue]);
	if (!keys.length) return null;

	async function loadLatest() {
		const before = cfg.content;
		const r = await refetchConfig();
		const latest = r?.content;
		if (!r || !latest) return;
		const cur = getDraftState();
		const drafts: Record<string, string> = {};
		const overlap: string[] = [];
		for (const [k, v] of Object.entries(cur.drafts)) {
			if (v === asText(latest[k])) continue; // 对方改成了和我一样的值
			drafts[k] = v;
			if (asText(before[k]) !== asText(latest[k])) overlap.push(labelOf(r, k));
		}
		patchDraftState({
			drafts,
			base: r.snapshot_id,
			issue: null,
			overlap,
			saving: false,
		});
		if (!Object.keys(drafts).length) resetDrafts();
	}
	async function refetchConfig() {
		const r = await cfgQ.refetch();
		return r.data;
	}

	const conflictRow =
		issue?.kind === "conflict"
			? snaps.data?.rows.find((r) => r.snapshot_id === issue.activeId)
			: undefined;

	return (
		<Card className="sticky top-0 z-20 shadow-sm" data-testid="plan-block-unsaved">
			<CardContent className="space-y-2 px-4 py-3 text-sm">
				<div className="flex flex-wrap items-center gap-2" data-testid="plan-prompts-dirty">
					<PencilLine className="h-4 w-4 text-brand-accent" aria-hidden />
					<span className="font-medium text-text-primary">有 {keys.length} 处未保存修改：</span>
					{keys.map((k) => (
						<Badge key={k} tone="accent">
							{labelOf(cfg, k)}
						</Badge>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Input
						ref={noteRef}
						value={st.note}
						disabled={st.saving}
						onChange={(e) => patchDraftState({ note: e.target.value })}
						placeholder="备注（可选）：这次为什么改"
						className={`max-w-xl flex-1 ${issue?.kind === "note" ? "ring-2 ring-brand-warning" : ""}`}
						data-testid="plan-prompts-note"
					/>
					{st.saving ? <span className="text-xs text-text-muted">正在保存为新快照…</span> : null}
					<Button
						type="button"
						variant="ghost"
						size="sm"
						disabled={st.saving}
						onClick={discardAll}
						className="ml-auto"
						data-testid="plan-prompts-discard-all"
					>
						放弃全部修改
					</Button>
				</div>
				{issue ? (
					<div
						className="flex flex-wrap items-start gap-2 rounded-md bg-brand-warning/10 px-3 py-2 text-xs text-text-primary"
						data-testid="plan-prompts-issue"
						data-issue={issue.kind}
					>
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-warning" aria-hidden />
						<div className="min-w-0 flex-1 space-y-1.5">
							{issue.kind === "conflict" ? (
								<>
									<div className="flex flex-wrap items-center gap-1">
										保存没成功：在你修改期间，配置已被保存为
										<SnapshotTag id={issue.activeId} />
										{conflictRow
											? `（${conflictRow.operator ?? "—"} · ${fmtAt(conflictRow.at)}）`
											: ""}
										。
									</div>
									<div>
										你的 {keys.length} 处修改还在页面上，没丢。先看看对方改了什么，再决定是否覆盖。
									</div>
									<div className="flex flex-wrap gap-2">
										<Button asChild type="button" variant="outline" size="sm">
											<a
												href={`/plan-lab?snapshot=${encodeURIComponent(issue.activeId)}#config-versions`}
												target="_blank"
												rel="noreferrer"
												data-testid="plan-prompts-conflict-view"
											>
												查看对方改了什么
											</a>
										</Button>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={loadLatest}
											data-testid="plan-prompts-load-latest"
										>
											载入最新并保留我的修改
										</Button>
									</div>
								</>
							) : null}
							{issue.kind === "invalid"
								? issue.items.map((i) => (
										<div key={`${i.key}:${i.reason}`}>
											保存没成功：{i.label} {i.reason}。改好后再保存。
										</div>
									))
								: null}
							{issue.kind === "note" ? (
								<div>
									保存没成功：{issue.labels.length ? `改了${issue.labels.join("、")}，` : ""}
									这次改动要写备注，写好后再保存。
								</div>
							) : null}
							{issue.kind === "error" ? (
								<div className="flex flex-wrap items-center gap-2">
									<span>保存没成功（{issue.message}）。你的修改还在。</span>
									<Button type="button" variant="outline" size="sm" onClick={run}>
										重试
									</Button>
								</div>
							) : null}
						</div>
					</div>
				) : null}
				{!issue && st.overlap ? (
					<div className="text-xs text-text-secondary" data-testid="plan-prompts-overlap">
						已载入最新配置 <SnapshotTag id={st.base} />
						{st.overlap.length
							? `；这几项对方也改了，以你的为准：${st.overlap.join("、")}`
							: "，你的修改保留在页面上"}
						。确认后再保存。
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}

/** 输入字段规格（证据层，默认收起）：变量取自默认稿与当前稿里的 {{变量}}，看当前草稿是否还带着 */
function VarSpecTable({
	vars,
	current,
}: {
	vars: { name: string; part: Part }[];
	current: string;
}) {
	const [open, setOpen] = useState(false);
	if (!vars.length) return null;
	const missing = vars.filter((v) => !current.includes(`{{${v.name}}}`)).length;
	return (
		<div className="rounded-lg border border-surface-border">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex w-full flex-wrap items-center gap-2 px-4 py-2.5 text-left"
			>
				<span className="text-xs font-medium text-text-primary">输入字段规格（{vars.length}）</span>
				{missing ? <Pill tone="warning">当前稿缺 {missing} 个</Pill> : null}
				<span className="ml-auto text-xs text-text-muted">{open ? "收起" : "展开"}</span>
			</button>
			{open ? (
				<div className="border-t border-surface-border p-3">
					<PlanTable
						density="compact"
						rows={vars}
						rowKey={(v) => v.name}
						columns={[
							{
								key: "name",
								title: "字段",
								render: (v) => (
									<code className="font-mono text-xs text-text-primary">{v.name}</code>
								),
							},
							{
								key: "part",
								title: "默认写在",
								render: (v) => <span className="text-xs text-text-secondary">{v.part} 段</span>,
							},
							{
								key: "has",
								title: "当前稿",
								render: (v) =>
									current.includes(`{{${v.name}}}`) ? (
										<Badge tone="success">有</Badge>
									) : (
										<Badge tone="warning">缺</Badge>
									),
							},
						]}
					/>
				</div>
			) : null}
		</div>
	);
}

function PromptPartEditor({
	part,
	value,
	saved,
	defaultValue,
	defaultsFailed,
	vars,
	error,
	disabled,
	onChange,
	onDiscard,
}: {
	part: Part;
	value: string;
	saved: string;
	defaultValue: string | null;
	defaultsFailed: boolean;
	vars: string[];
	error?: string;
	disabled: boolean;
	onChange: (v: string) => void;
	onDiscard: () => void;
}) {
	const taRef = useRef<HTMLTextAreaElement | null>(null);
	const [varOpen, setVarOpen] = useState(false);
	const [varQuery, setVarQuery] = useState("");
	const dirty = value !== saved;
	const overridden = defaultValue != null && saved !== defaultValue;
	const isDefault = defaultValue != null && value === defaultValue;
	const resetReason =
		defaultValue == null
			? defaultsFailed
				? "默认值没取到"
				: "正在取默认值"
			: isDefault
				? "已是默认"
				: null;
	const shownVars = vars.filter((v) => !varQuery || v.includes(varQuery));
	// 变量插入弹层——光标处插 {{name}}
	function insertVar(name: string) {
		const ta = taRef.current;
		const token = `{{${name}}}`;
		if (!ta) {
			onChange(value + token);
		} else {
			const s = ta.selectionStart ?? value.length;
			const e = ta.selectionEnd ?? value.length;
			onChange(value.slice(0, s) + token + value.slice(e));
			requestAnimationFrame(() => {
				ta.focus();
				ta.selectionStart = ta.selectionEnd = s + token.length;
			});
		}
		setVarOpen(false);
		setVarQuery("");
	}
	return (
		<div
			className={`space-y-2 rounded-md border p-3 ${error ? "border-brand-warning" : "border-surface-border"}`}
			data-testid={`plan-prompt-part-${part}`}
			data-dirty={dirty ? "1" : "0"}
		>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="flex flex-wrap items-center gap-2 text-sm font-medium text-text-primary">
					<Pill tone={part === "system" ? "muted" : "info"}>{PART_LABEL[part]}</Pill>
					{dirty ? <Badge tone="accent">已修改</Badge> : null}
					{overridden ? <Badge tone="neutral">已改过默认</Badge> : null}
				</span>
				<div className="flex gap-2">
					{dirty ? (
						<Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={onDiscard}>
							放弃本块修改
						</Button>
					) : null}
					<Button
						type="button"
						size="sm"
						variant="outline"
						disabled={disabled || !!resetReason}
						title={resetReason ?? "把本块草稿填回默认值（保存为新快照后才生效）"}
						onClick={() => defaultValue != null && onChange(defaultValue)}
					>
						重置为默认
					</Button>
				</div>
			</div>
			{error ? (
				<div
					className="flex items-center gap-1.5 text-xs text-brand-warning"
					data-testid="plan-prompt-part-error"
				>
					<AlertTriangle className="h-3.5 w-3.5" aria-hidden />
					{error}
				</div>
			) : null}
			{vars.length ? (
				<div className="relative flex flex-wrap items-center gap-1.5">
					<Button type="button" size="sm" variant="ghost" onClick={() => setVarOpen((o) => !o)}>
						<span className="font-mono">{"<>"}</span> 插入变量
					</Button>
					<span className="text-xs text-text-muted">
						{vars.length} 个可用变量 · 光标处插入 {"{{变量}}"} · 各块输入从哪来见{" "}
						<PageJump to="info-flow" />
					</span>
					{varOpen ? (
						<div className="absolute left-0 top-8 z-30 w-72 rounded-lg bg-surface-card p-2 shadow-lg ring-1 ring-surface-border">
							<Input
								ref={(el) => el?.focus()}
								value={varQuery}
								onChange={(e) => setVarQuery(e.target.value)}
								placeholder="搜索变量…"
								className="mb-1"
							/>
							<div className="max-h-56 overflow-y-auto">
								{shownVars.map((v) => (
									<button
										key={v}
										type="button"
										onClick={() => insertVar(v)}
										className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left transition-colors duration-150 hover:bg-surface-subtle"
									>
										<code className="font-mono text-xs text-text-primary">{v}</code>
									</button>
								))}
								{!shownVars.length ? (
									<div className="px-2 py-3 text-xs text-text-muted">无匹配变量</div>
								) : null}
							</div>
						</div>
					) : null}
				</div>
			) : null}
			<Textarea
				ref={taRef}
				className={`font-mono text-xs ${part === "system" ? "h-64" : "h-36"}`}
				value={value}
				disabled={disabled}
				onChange={(e) => onChange(e.target.value)}
				data-testid={`plan-prompt-text-${part}`}
			/>
		</div>
	);
}

const RANK_ENGINE_KEY = "plan.rank.engine";
const RANK_ENGINE_LABEL: Record<string, string> = {
	jev: "Jev 重排服务（TypeSafe）",
	llm: "大模型（按同样题型判）",
};

/** 排序服务：改了只进草稿，跟页级保存一起进快照；Jev 调用失败时按粗召回顺序继续 */
function RankEngineSelect({
	cfg,
	value,
	disabled,
	onChange,
}: {
	cfg: PlanConfig;
	value: string;
	disabled: boolean;
	onChange: (v: string) => void;
}) {
	const saved = asText(cfg.content[RANK_ENGINE_KEY]);
	return (
		<div
			className="flex flex-wrap items-center gap-2 rounded-md bg-surface-subtle/60 p-2.5 text-xs"
			data-testid="plan-rank-engine"
		>
			<span className="font-medium text-text-primary">排序服务</span>
			<StyledSelect value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
				{!(value in RANK_ENGINE_LABEL) ? (
					<option value={value}>当前 · {value || "未设"}</option>
				) : null}
				{Object.entries(RANK_ENGINE_LABEL).map(([k, label]) => (
					<option key={k} value={k}>
						{label}
					</option>
				))}
			</StyledSelect>
			{value !== saved ? <Badge tone="accent">已修改</Badge> : null}
			<span className="text-text-muted">Jev 调用失败时按粗召回顺序继续</span>
		</div>
	);
}

/** 节点模型：改了只进草稿，跟页级保存一起进快照（D-27 判定总表 D 项） */
function NodeModelSelect({
	node,
	cfg,
	value,
	disabled,
	onChange,
	defaultValue,
}: {
	node: PromptNodeKey;
	cfg: PlanConfig;
	value: string;
	disabled: boolean;
	onChange: (v: string) => void;
	defaultValue: string | null;
}) {
	const saved = asText(cfg.content[modelKey(node)]);
	const options = cfg.model_options;
	const inList = options.some((o) => o.key === value);
	const noOptions = !options.length;
	return (
		<div
			className="flex flex-wrap items-center gap-2 rounded-md bg-surface-subtle/60 p-2.5 text-xs"
			data-testid="plan-node-model"
		>
			<span className="font-medium text-text-primary">模型</span>
			<StyledSelect
				value={value}
				disabled={disabled || noOptions}
				title={noOptions ? "没取到可选模型" : undefined}
				onChange={(e) => onChange(e.target.value)}
			>
				{!inList ? (
					<option value={value}>
						当前 · {value}
						{noOptions ? "" : "（当前提供方不可用，调用会失败）"}
					</option>
				) : null}
				{options.map((o) => (
					<option key={o.key} value={o.key}>
						{o.label}
					</option>
				))}
			</StyledSelect>
			{value !== saved ? <Badge tone="accent">已修改</Badge> : null}
			{noOptions ? <span className="text-text-muted">没取到可选模型</span> : null}
			{defaultValue ? <span className="text-text-muted">默认 {defaultValue}</span> : null}
		</div>
	);
}

function NodeBlock({
	node,
	cfg,
	defaults,
	defaultsFailed,
}: {
	node: PromptNodeKey;
	cfg: PlanConfig;
	defaults: Record<string, unknown> | null;
	defaultsFailed: boolean;
}) {
	const st = useDraftState();
	const base = cfg.snapshot_id;
	const valOf = (key: string) => st.drafts[key] ?? asText(cfg.content[key]);
	const vars = useMemo(() => {
		const out: { name: string; part: Part }[] = [];
		for (const part of ["system", "user"] as const) {
			const k = promptKey(node, part);
			for (const name of varsIn(`${asText(defaults?.[k])}\n${asText(cfg.content[k])}`)) {
				if (!out.some((v) => v.name === name)) out.push({ name, part });
			}
		}
		return out;
	}, [node, defaults, cfg.content]);
	const current = `${valOf(promptKey(node, "system"))}\n${valOf(promptKey(node, "user"))}`;
	const errorOf = (key: string) =>
		st.issue?.kind === "invalid"
			? st.issue.items
					.filter((i) => i.key === key)
					.map((i) => i.reason)
					.join("；") || undefined
			: undefined;
	const mk = modelKey(node);
	return (
		<Card data-testid={`plan-block-node-${node}`}>
			<CardContent className="space-y-3 p-4">
				<div className="space-y-0.5">
					<div className="text-sm font-semibold text-text-primary">{NODE_LABEL_ZH[node].title}</div>
					<div className="text-xs text-text-muted">{NODE_LABEL_ZH[node].sub}</div>
				</div>
				{node === "rank" ? (
					<RankEngineSelect
						cfg={cfg}
						value={valOf(RANK_ENGINE_KEY)}
						disabled={st.saving}
						onChange={(v) =>
							setDraft(RANK_ENGINE_KEY, v, asText(cfg.content[RANK_ENGINE_KEY]), base)
						}
					/>
				) : null}
				<NodeModelSelect
					node={node}
					cfg={cfg}
					value={valOf(mk)}
					disabled={st.saving}
					defaultValue={defaults ? asText(defaults[mk]) : null}
					onChange={(v) => setDraft(mk, v, asText(cfg.content[mk]), base)}
				/>
				<VarSpecTable vars={vars} current={current} />
				{(["system", "user"] as const).map((part) => {
					const key = promptKey(node, part);
					return (
						<PromptPartEditor
							key={key}
							part={part}
							value={valOf(key)}
							saved={asText(cfg.content[key])}
							defaultValue={defaults ? asText(defaults[key]) : null}
							defaultsFailed={defaultsFailed}
							vars={vars.map((v) => v.name)}
							error={errorOf(key)}
							disabled={st.saving}
							onChange={(v) => setDraft(key, v, asText(cfg.content[key]), base)}
							onDiscard={() => discardDraft(key)}
						/>
					);
				})}
				{node === "rank" ? (
					<PromptPartEditor
						key={JEV_QUESTION_KEY}
						part="jev"
						value={valOf(JEV_QUESTION_KEY)}
						saved={asText(cfg.content[JEV_QUESTION_KEY])}
						defaultValue={defaults ? asText(defaults[JEV_QUESTION_KEY]) : null}
						defaultsFailed={defaultsFailed}
						vars={[]}
						error={errorOf(JEV_QUESTION_KEY)}
						disabled={st.saving}
						onChange={(v) =>
							setDraft(JEV_QUESTION_KEY, v, asText(cfg.content[JEV_QUESTION_KEY]), base)
						}
						onDiscard={() => discardDraft(JEV_QUESTION_KEY)}
					/>
				) : null}
				{node === "rank"
					? JEV_EXTRA_KEYS.map(([part, key]) => (
							<PromptPartEditor
								key={key}
								part={part}
								value={valOf(key)}
								saved={asText(cfg.content[key])}
								defaultValue={defaults ? asText(defaults[key]) : null}
								defaultsFailed={defaultsFailed}
								vars={[]}
								error={errorOf(key)}
								disabled={st.saving}
								onChange={(v) => setDraft(key, v, asText(cfg.content[key]), base)}
								onDiscard={() => discardDraft(key)}
							/>
						))
					: null}
			</CardContent>
		</Card>
	);
}

export function PromptsPanel() {
	const cfg = usePlanConfig();
	const defaults = usePlanDefaults();
	const st = useDraftState();
	const dirtyCount = Object.keys(st.drafts).length;

	// 有未保存修改时关页 ／ 刷新 → 浏览器原生离开提示（线框 04 (g)）；plan 内切菜单草稿留在模块里，不丢
	useEffect(() => {
		if (!dirtyCount) return;
		const h = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = `有 ${dirtyCount} 处修改没保存，离开会丢失`;
		};
		window.addEventListener("beforeunload", h);
		return () => window.removeEventListener("beforeunload", h);
	}, [dirtyCount]);

	if (cfg.isLoading) {
		return (
			<Card data-testid="plan-block-prompts">
				<CardContent className="p-6">
					<LoadingInline text="加载提示词与模型…" />
				</CardContent>
			</Card>
		);
	}
	if (cfg.isError || !cfg.data) {
		// 整页失败态：不显示任何编辑块，避免在旧值上改（线框 04 (h)）
		return (
			<Card data-testid="plan-block-prompts">
				<CardContent className="p-0">
					<FailState
						title="提示词与模型没加载出来"
						reason={cfg.error ? planErrorMessage(cfg.error) : "没有取到配置"}
						onRetry={() => cfg.refetch()}
						retrying={cfg.isFetching}
						testId="plan-prompts-fail"
					/>
				</CardContent>
			</Card>
		);
	}
	const data = cfg.data;
	return (
		<>
			<CurrentSnapshot cfg={data} />
			<UnsavedBar cfg={data} />
			{PROMPT_NODE_KEYS.map((node) => (
				<NodeBlock
					key={node}
					node={node}
					cfg={data}
					defaults={defaults.content}
					defaultsFailed={defaults.failed}
				/>
			))}
		</>
	);
}
